import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, FlatList, SectionList, SafeAreaView, ScrollView } from 'react-native'
import { format } from 'date-fns'
import { Icon, Text, Card, Datepicker, Spinner, Button, CheckBox, Modal, RadioGroup, Radio, OverflowMenu } from '@ui-kitten/components'
import HeaderIconButton from '../../components/HeaderIconButton'
import { UserContext } from '../../global/UserProvider'
import { getLatestChecklistBeforeDate, addChecklist, updateChecklist } from '../../global/Firebase/firebase'
import { snapshotExists } from '../../global/Firebase/types'
import StyledTextInput from '../../components/StyledTextInput'
import { useColors } from '../../utils/useColors'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { localeDateServiceAtom, storesDictAtom, userRoleAtom, usersDictAtom } from '../../global/AtomsProvider'
import { getLocalStoreDate } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'

export default function SellerChecklistScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const colors = useColors()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const usersDict = useAtomValue(usersDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const [date, setDate] = useState(getLocalStoreDate())
  const [checklist, setChecklist] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [store, setStore] = useState(context.user.store?.id)
  const [addTaskModalIsVisible, setAddTaskModalIsVisible] = useState(false)
  const _rangePicker = React.createRef()  
  const [editItem, setEditItem] = useState(null)
  const [editItemIndex, setEditItemIndex] = useState(null)
  const [newTaskText, setNewTaskText] = useState('')
  const [newTaskType, setNewTaskType] = useState('task')
  const [newTaskIndex, setNewTaskIndex] = useState(0)
  const [newTaskIndexValue, setNewTaskIndexValue] = useState(null)
  const [saveError, setSaveError] = useState(null)
  let refs = []
  
  useEffect(() => {    
    _rangePicker.current?.blur()
    setIsLoading(true)
    setChecklist(null)
    var docID = format(date, "d-M-y") + '-' + store + '-seller'
    // if (store !== 'manager') {
    //   docID += '-seller'
    // }
    const unsubscribe = db.collection('checklists').doc(docID).onSnapshot((querySnapshot) => {
      if (snapshotExists(querySnapshot)) {
        var newChecklist = querySnapshot.data()
        newChecklist.id = querySnapshot.id
        newChecklist.ref = querySnapshot.ref
        setChecklist({...newChecklist, id: docID})
        setIsLoading(false)
      } else {
        createNewChecklist(docID, date, store)
      }
    })

    if ((userRole?.permissions?.['editChecklistItems'] || context.user.role === 'admin')) {
      navigation.setOptions({
        headerRight: () => (
          <HeaderIconButton
            onPress={() => {
              setEditItem(null)
              setEditItemIndex(null)
              setNewTaskIndex(0)
              setNewTaskText('')
              setNewTaskType('task')
              setNewTaskIndexValue(null)
              setAddTaskModalIsVisible(true)
            }}
            iconName='plus-outline'
          />
        ),
      })
    }

    return () => {
      unsubscribe()
    }

  }, [date, store])

  const EditIcon = (style) => (
    <Icon {...style} name='edit-outline'/>
  );

  const createNewChecklist = async (docID, date) => {
    // copy regular tasks and incomplete extra tasks from yesterday
    var newContent = []      
    const latestChecklist = await getLatestChecklistBeforeDate(db, date, store)
    if (latestChecklist) {
      for (const [index, value] of latestChecklist.content.entries()) {
        if (value.type === 'task') {
          newContent.push({type: value.type, text: value.text, done: false})
        } else if (value.type === 'header') {
          newContent.push({type: value.type, text: value.text})
        } else if (value.type === 'extra' && !value.done) {
          newContent.push(value)
        }
      }
      var newChecklist = {
        date, 
        store,
        content: newContent,
        organizationId: latestChecklist.organizationId,
      }
      try {
        await addChecklist(db, docID, newChecklist)
        setIsLoading(false)
      } catch(error) {
        Sentry.captureException(error)
        console.log(error)
        setIsLoading(false)
      }
    } else {
      // newContent = [...emptyManagerChecklist]
      alert(t('checklist.checklistNotAvailableForToday'))
    }
  }

  const markChecked = (item, index, isChecked) => {
    var newContent = [...checklist.content]
    if (isChecked) {
      newContent[index] = {...newContent[index], done: isChecked, userDone: context.user.uid, dateDone: new Date()}
    } else {
      newContent[index] = {...newContent[index], done: isChecked, userDone: null, dateDone: null}
    }
    // setChecklist({...checklist, content: newContent})
    updateChecklist(db, checklist.id, {...checklist, content: newContent})
  }

  const RightSwipeActions = (item, index) => {
    return (
      <View
        style={{
          backgroundColor: colors.red,
          justifyContent: 'center',
          paddingRight: 10,
          paddingLeft: 15,
          marginBottom: 5,
          borderRadius: 4,
          marginLeft: -10,
        }}
      >
        <Button appearance='ghost' accessoryLeft={(style) => (
          <Icon {...style} name='trash-outline' fill='#FFFFFF'/>
        )} onPress={() => {
          refs[index].close()
          var newContent = [...checklist.content]
          newContent.splice(index, 1)
          // setChecklist({...checklist, content: newContent})
          updateChecklist(db, checklist.id, {...checklist, content: newContent})
          refs[index].close()
        }} />
      </View>
    );
  };
  
  const renderItem = ({item, index}) => {
    return (
      <View style={{backgroundColor: colors.white, borderRadius: 4, paddingHorizontal: 20, paddingVertical: 10, flexDirection: 'row', alignItems: 'center', marginBottom: 5}}>
        { item.type === 'header' ? (
          <Text category='label' style={{flex: 1}}>{item.text}</Text>
        ) : ( 
          <CheckBox 
            checked={item.done}
            onChange={(isChecked) => {
              markChecked(item, index, isChecked)
            }}  
            style={{flex: 1}}
          >
            {item.text + (item.userDone && item.dateDone ? (' (' + usersDict[item.userDone]?.name + ' ' + format((item.dateDone instanceof Date ? item.dateDone : item.dateDone.toDate()), 'HH:mm') + ')') : '')}        
          </CheckBox>
        )} 
        <Button style={styles.button} appearance='ghost' status='info' accessoryLeft={EditIcon} onPress={() => {
          setEditItem(item)
          setEditItemIndex(index)
          setNewTaskIndex(index)
          setNewTaskText(item.text)
          setNewTaskType(item.type)
          setNewTaskIndexValue(item)
          setAddTaskModalIsVisible(true)
        }} />
      </View>
    )
  }

  return (
    <View style={styles.container}>
      {
        (userRole?.permissions?.['editChecklistItems'] || context.user.role === 'admin') && (
          <View style={styles.dateFilter}>
            <Text category='label' style={styles.label}>
              {t('filterByDate')}
            </Text>
            <Datepicker
              placement='top'
              min={new Date('2020-12-01')}
              max={new Date('2029-12-31')}
              dateService={localeDateService}
              date={date}
              ref={_rangePicker}
              onSelect={(date) => {
                setDate(date)
              }}
            />
            { Object.keys(storesDict).length > 1 && (
              <View style={styles.storeChooser}>
                { Object.entries(storesDict).map(([key, value]) => (
                  <Button style={styles.storeButton} key={key} appearance='outline' status={store === key ? 'primary' : 'basic'} onPress={() => {
                    setStore(key)
                  }}>{value.name}</Button>
                ))}
              </View>
            )}
          </View>
        )
      }
      {
        isLoading
          ? (
            <View style={{alignItems: 'center', marginTop: 10}}><Spinner size="large" /></View>
          )
          : (
            <FlatList
              style={styles.list}
              data={checklist?.content}
              keyExtractor={(item, index) => item + index}
              renderItem={renderItem}
              ListEmptyComponent={() => (<Text appearance='hint' style={styles.noItemsText}>
                  {t('noItemsText')}
                </Text>
              )}
              ListFooterComponent={() => <View style={{height: 50}}></View>}
            />
          )
      }
    <Modal style={{flex: 1, top: 100,}} 
      visible={addTaskModalIsVisible} 
      backdropStyle={{backgroundColor: 'rgba(0, 0, 0, 0.5)'}} 
      onBackdropPress={() => setAddTaskModalIsVisible(false)}
    >
      <Card style={{
        flex: 1,
        justifyContent: 'space-around',
        alignItems: 'stretch',
        width: 300,
        height: '80%',
        padding: 16,
        margin: 32, 
        paddingBottom: 100,
      }}>
      <ScrollView style={{flex: 1}}>
        <Text category='h2' style={{marginVertical: 4}}>
          {editItem ? t('checklist.editTask') : t('checklist.addTask')}
        </Text>
        <StyledTextInput
          value={newTaskText}
          onChangeText={setNewTaskText}
          style={{marginVertical: 4}}
          returnKeyType='done'
          autoCapitalize='sentences'
        />
        <Text category='label' style={{marginVertical: 4}}>
          {t('checklist.type')}
        </Text>
        <Radio style={styles.radio} checked={newTaskType === 'extra'} onChange={() => {setNewTaskType('extra')}}>
          {t('checklist.oneTimeTask')}
        </Radio>
        <Radio style={styles.radio} checked={newTaskType === 'task'} onChange={() => {setNewTaskType('task')}}>
          {t('checklist.dailyTask')}
        </Radio>
        <Radio checked={newTaskType === 'header'} onChange={() => {setNewTaskType('header')}}>
          {t('checklist.section')}
        </Radio>
        <Text category='label' style={{marginVertical: 4}}>
          {t('checklist.positionBefore')}
        </Text>
        <PlatformDependantSelect
          data={checklist?.content}
          selectedOption={newTaskIndexValue}
          onSelect={(itemValue) => {
            for (const [index, value] of checklist.content.entries()) {
              if (value.text === itemValue.text) {
                setNewTaskIndex(index)
                break
              }
            }
            setNewTaskIndexValue(itemValue)
          }}
          style={{marginVertical: 4}}
        />
        {saveError && (
            <Text category='p1' status='danger' style={{marginVertical: 4}}>
              {saveError}
            </Text>
          )}
        <Button style={{marginVertical: 4}} onPress={() => {
          setSaveError(null)
          if (newTaskText === '') {
            setSaveError(t('notAllFieldsFilled'))
          } else {
            var newContent = [...checklist.content]
            var newTask = {
              text: newTaskText,
              type: newTaskType
            }
            if (editItem) {
              newContent.splice(editItemIndex, 1) // delete the task from array
              newContent.splice(newTaskIndex, 0, newTask) // insert it into different location
            } else {
              newContent.splice(newTaskIndex, 0, newTask)
            }
            // setChecklist({...checklist, content: newContent})
            updateChecklist(db, checklist.id, {...checklist, content: newContent})
            setNewTaskText('')
            setNewTaskIndex(0)
            setNewTaskIndexValue(null)
            setAddTaskModalIsVisible(false)
          }
        }}>
          {t('save')}
        </Button>
        { editItem && (
          <Button status='danger' style={{marginVertical: 4}} onPress={() => {
            var newContent = [...checklist.content]
            newContent.splice(newTaskIndex, 1)
            updateChecklist(db, checklist.id, {...checklist, content: newContent})
            setNewTaskText('')
            setNewTaskIndex(0)
            setNewTaskIndexValue(null)
            setAddTaskModalIsVisible(false)
          }}>
            {t('delete')}
          </Button>
        )}
        <Button status='basic' style={{marginVertical: 4}} onPress={() => setAddTaskModalIsVisible(false)}>
          {t('cancel')}
        </Button>
        </ScrollView>
      </Card>
    </Modal>

    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dateFilter: {
    marginHorizontal: 20,
  },
  list: {
    flex: 1,
    paddingHorizontal: 20,
    marginTop: 20,
  },
  noItemsText: {
    marginTop: 100,
    textAlign: 'center'
  },
  label: {
    marginTop: 10,
    marginBottom: 5
  },
  storeChooser: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
    marginTop: 10,
    justifyContent: 'center'
  }, 
  storeButton: {
    marginLeft: 5,
    marginBottom: 5,
  },
  radio: {
    marginBottom: 5
  },
});
